import { BuilderComponent, useIsPreviewing } from "@builder.io/react";
import { BuilderContent } from "@builder.io/sdk";
import { useTranslation } from "next-i18next";
import React, { useMemo } from "react";

import { NEXT_PAGE_REVALIDATE } from "@/shared/constants";

import { parallel } from "@/utils/promises";
import serverSideTranslations from "@/utils/serverSideTranslations";

import { init } from "@/builder";
import { BuilderModel } from "@/builder/types";

import { ChunkedNav, GenericNavItem } from "@/interfaces/navigation-interface";

import { Locale } from "@/types/locale";
import { GetStaticProps } from "@/types/page";

import Error from "../components/error/error";
import BareLayout from "../layouts/bare";
import DefaultLayout from "../layouts/default";
import { getBuilderMessages, getBuilderNavLinks, getBuilderPage } from "./api/builder/builder";
import { getFooterNavigation, getHeaderNavigation } from "./api/store-directory/navigation";

type LocaleProps = {
    locale: Locale;
};

type NavProps = {
    headerNavigationItems: ChunkedNav;
    footerLocalNavItems: ChunkedNav;
    footerRegionalNavItems: Array<Array<GenericNavItem>>;
};

type OwnProps = {
    builderContent: BuilderContent;
    builderMessages: Array<BuilderContent>;
    builderNavLinks: Array<BuilderContent>;
};

type Props = OwnProps & NavProps & LocaleProps;

init(BuilderModel.PAGE);
init(BuilderModel.MESSAGE);

export const getStaticProps: GetStaticProps<Props> = async ({ locale }) => {
    const pagePath = "/";

    const parallelProps = await parallel({
        builderContent: () => getBuilderPage(pagePath, locale),
        headerNavigationItems: () => getHeaderNavigation(locale),
        footerNavItems: () => getFooterNavigation(locale),
        builderMessages: () => getBuilderMessages(locale),
        builderNavLinks: () => getBuilderNavLinks(locale),
        _nextI18Next: () => serverSideTranslations(locale),
    });

    return {
        revalidate: NEXT_PAGE_REVALIDATE,
        props: {
            locale,
            footerLocalNavItems: parallelProps.footerNavItems[0],
            footerRegionalNavItems: parallelProps.footerNavItems[1],
            ...parallelProps,
        },
    };
};

const FourZeroFour: React.FC<Props> = ({
    locale,
    headerNavigationItems,
    footerLocalNavItems,
    footerRegionalNavItems,
    builderContent,
    builderMessages,
    builderNavLinks,
}) => {
    const { t } = useTranslation();
    const errorHeading = t("errorPages.404.heading"),
        errorSubHeading = t("errorPages.404.subHeading"),
        errorCTALabel = t("errorPages.404.cta");
    const isPreviewing = useIsPreviewing();

    const renderError = useMemo(
        () => (
            <Error
                heading={errorHeading}
                subHeading={errorSubHeading}
                ctaLabel={errorCTALabel}
                ctaUrl={"/"}
            />
        ),
        [errorHeading, errorSubHeading, errorCTALabel]
    );

    const renderBuilderComponent = () => <BuilderComponent model={BuilderModel.PAGE} />;

    if (locale === "global") {
        return <BareLayout>{renderError}</BareLayout>;
    }

    return (
        <DefaultLayout
            currentLocale={locale}
            headerNavigationItems={headerNavigationItems}
            footerLocalNavItems={footerLocalNavItems}
            footerRegionalNavItems={footerRegionalNavItems}
            title={builderContent?.data.title}
            description={builderContent?.data.description}
            builderMessages={builderMessages}
            builderNavLinks={builderNavLinks}
        >
            {isPreviewing ? renderBuilderComponent() : renderError}
        </DefaultLayout>
    );
};

export default FourZeroFour;
